import { Play } from "@untitledui/icons";
import { type RefObject, useEffect, useRef, useState } from "react";
import { useVideoPlayback } from "@/api/assurance/video";
import { Button } from "@/components/base/buttons/button";

interface VideoEmbedProps {
  url?: string | null;
  /**
   * Enables playback from our stored copy when TikTok's player can't show the post.
   * Without it, those posts render as the fallback.
   */
  videoId?: string | null;
  /**
   * Optional thumbnail to show as the fallback preview when TikTok refuses
   * to embed the video. Strongly recommended at every call site — without it
   * the fallback is just a gray box with a CTA.
   */
  thumbnail?: string | null;
  noScroll?: boolean;
  // Below: opt into the container-driven sizing (used by the safety-beta video card, whose
  // width resizes with its container). Passing any of these switches the whole render to that
  // variant — omit all three to keep the original fixed-scale rendering every other caller relies on.
  width?: string;
  height?: string;
  containerClassName?: string;
}

const TIKTOK_ORIGIN = "https://www.tiktok.com";

/** The player reports `onPlayerReady` once it has loaded. A post it can't show may never report it. */
const PLAYER_READY_TIMEOUT_MS = 10_000;

/**
 * `onPlayerError` codes meaning the post can't be shown. AUTOPLAY_ERROR (3002) is left out:
 * the browser only blocked autoplay, and the post still plays once the viewer presses play.
 */
const UNPLAYABLE_ERROR_CODES = new Set([1001, 2001, 3001]);

const getTikTokVideoId = (url?: string | null): string | undefined => {
  if (!url) return undefined;
  const match = url.match(/\/video\/(\d+)/);
  return match?.[1];
};

const usePlayerFailed = (iframeRef: RefObject<HTMLIFrameElement | null>, playerUrl?: string) => {
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    setFailed(false);
    if (!playerUrl) return;

    const timeout = setTimeout(() => setFailed(true), PLAYER_READY_TIMEOUT_MS);
    const handleMessage = (event: MessageEvent) => {
      if (event.origin !== TIKTOK_ORIGIN || event.source !== iframeRef.current?.contentWindow) return;
      if (event.data?.type === "onPlayerReady") clearTimeout(timeout);
      if (event.data?.type === "onPlayerError" && UNPLAYABLE_ERROR_CODES.has(event.data.value?.errorCode)) {
        clearTimeout(timeout);
        setFailed(true);
      }
    };

    window.addEventListener("message", handleMessage);
    return () => {
      clearTimeout(timeout);
      window.removeEventListener("message", handleMessage);
    };
  }, [iframeRef, playerUrl]);

  return failed;
};

const Fallback = ({ url, thumbnail }: { url?: string | null; thumbnail?: string | null }) => (
  <div className="relative h-full w-full">
    {thumbnail ? (
      <img src={thumbnail} alt="TikTok video preview" className="absolute inset-0 h-full w-full object-cover" />
    ) : (
      <div className="absolute inset-0 bg-gray-100" />
    )}
    <div className="absolute inset-0 flex flex-col items-center justify-center gap-3 bg-black/55 p-4 text-center backdrop-blur-[2px]">
      <Play className="size-10 text-white" />
      <p className="text-sm font-semibold text-white">Preview unavailable</p>
      <p className="px-2 text-xs text-white/85">
        TikTok blocked the embed for this video. You can still view it directly on TikTok.
      </p>
      {url ? (
        <Button color="primary" size="sm" href={url} target="_blank" rel="noopener noreferrer">
          View on TikTok
        </Button>
      ) : null}
    </div>
  </div>
);

export const VideoEmbed = ({
  url,
  videoId,
  thumbnail,
  noScroll,
  width,
  height,
  containerClassName,
}: VideoEmbedProps) => {
  const tiktokVideoId = getTikTokVideoId(url);
  const playerUrl = tiktokVideoId ? `${TIKTOK_ORIGIN}/player/v1/${tiktokVideoId}?autoplay=1` : undefined;

  const iframeRef = useRef<HTMLIFrameElement>(null);
  const playerFailed = usePlayerFailed(iframeRef, playerUrl);

  const { data: playback, isLoading: isResolvingPlayback } = useVideoPlayback(
    !playerUrl || playerFailed ? (videoId ?? "") : "",
  );
  const playUrl = playback?.playUrl;

  const content = playUrl ? (
    <video src={playUrl} autoPlay muted loop playsInline controls className="h-full w-full rounded-lg object-contain" />
  ) : isResolvingPlayback ? (
    <div className="h-full w-full" />
  ) : playerUrl && !playerFailed ? (
    <iframe
      ref={iframeRef}
      src={playerUrl}
      width="100%"
      height="100%"
      allow="autoplay; encrypted-media; fullscreen"
      className="rounded-lg"
      title="TikTok video"
      {...(noScroll ? { scrolling: "no" } : {})}
    />
  ) : (
    <Fallback url={url} thumbnail={thumbnail} />
  );

  if (width !== undefined || height !== undefined || containerClassName !== undefined) {
    return (
      <div
        className={`aspect-[9/16] overflow-hidden rounded-lg bg-white flex-shrink-0 relative ${containerClassName ? ` ${containerClassName}` : ""}`}
        // content-box so a padding class passed via containerClassName (e.g. reserving room for a
        // scrollbar) adds to the box's outer size instead of eating into the aspect-ratio'd video itself.
        style={height ? { height, boxSizing: "content-box" } : { width: width || "320px", boxSizing: "content-box" }}
      >
        {content}
      </div>
    );
  }

  return (
    <div className="w-[320px] scale-75 -mr-12 -mb-32 origin-top-left flex-shrink-0">
      <div className="aspect-[9/16] overflow-hidden rounded-lg bg-gray-100">{content}</div>
    </div>
  );
};
